/**
 * Where someone can be. The lobby is the corridor at `/`; every other place is a room behind
 * one of its doors, at its own path. Shared by the server (one set of rooms per place) and the
 * client (one scene per place, the address changing without a reload).
 */
export const LOBBY = 'lobby';

/**
 * A second entry here opens the corridor's right-hand door. A hall's screen shows infiniteslop
 * unless it names another source: `youtube: '<video id>'`, `kick: '<channel>'`,
 * `livekit: '<url that mints a viewer token>'`, or `twitch: '<channel>'` (laid flat, see TwitchScreen).
 */
export let ROOMS = Object.freeze({
  levelsio: Object.freeze({ path: '/levelsio', title: 'Infinite Slop' }),
  // The first open-source movie: its projector streams to a LiveKit room, and mints us the way in.
  openslop: Object.freeze({
    path: '/openslop',
    title: 'OpenSlop',
    livekit: 'https://www.openslop.live/api/infinite/token',
    plaque: Object.freeze({
      title: 'OPENSLOP',
      subtitle: 'the first open source movie',
      body: [
        'A film written by its audience and screened around the clock.',
        'Every scene is a pull request on GitHub, rendered live by AI.',
        'Write one, get it approved by other viewers, and it is on air',
        'in the next screening with your name on it.',
      ].join('\n'),
      footer: 'Add a scene: github.com/Dere-Wah/open-slop',
      url: 'https://github.com/Dere-Wah/open-slop',
    }),
  }),
  'lofi-girl': Object.freeze({
    path: '/lofi-girl',
    title: 'Lofi Girl',
    official: true,
    door_slot: 2,
    youtube: 'rFZHOHl-L8A',
    branding: Object.freeze({
      accent: '#d68f74',
      panel_subtitle: 'Hosted by The Infinite',
      panel_body: 'A continuous space for quiet listening and shared focus.',
    }),
  }),
});

export let PLACES = Object.freeze([LOBBY, ...Object.keys(ROOMS)]);
const listeners = new Set();
export function subscribePlaces(listener) { listeners.add(listener); return () => listeners.delete(listener); }
export function setRooms(rooms) {
  ROOMS = Object.freeze(Object.fromEntries(rooms.map(room => [room.slug, Object.freeze(room)])));
  PLACES = Object.freeze([LOBBY, ...Object.keys(ROOMS)]);
  for (const listener of listeners) listener();
}

export function isRoom(value) {
  return typeof value === 'string' && Object.hasOwn(ROOMS, value);
}

export function isPlace(value) {
  return value === LOBBY || isRoom(value);
}

/** The place a path names: a room's own path, or the lobby for anything else. */
export function placeAtPath(pathname) {
  const p = String(pathname || '/').replace(/\/+$/, '') || '/';
  for (const [id, room] of Object.entries(ROOMS)) {
    if (room.path === p) return id;
  }
  return LOBBY;
}

export function pathOf(place) {
  return isRoom(place) ? ROOMS[place].path : '/';
}
